import { useEffect, useMemo, useRef, useState } from "react";
import { hotEvents, latestFeed } from "@/mocks/chigua";

const NAV = [
  { label: "本周热榜", href: "#hot" },
  { label: "热门分类", href: "#cats" },
  { label: "事件时间线", href: "#timeline" },
  { label: "图集对比", href: "#gallery" },
  { label: "最新收录", href: "#latest" },
  { label: "常见问题", href: "#faq" },
];

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [kw, setKw] = useState("");
  const [focus, setFocus] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const results = useMemo(() => {
    const q = kw.trim().toLowerCase();
    if (!q) return [];
    const fromHot = hotEvents
      .filter((h) => h.title.toLowerCase().includes(q) || h.cat.toLowerCase().includes(q))
      .map((h) => ({ title: h.title, sub: `${h.cat} · ${h.updated}`, link: "#latest" }));
    const fromFeed = latestFeed
      .filter((f) => f.title.toLowerCase().includes(q) || f.cat.toLowerCase().includes(q) || f.tag.toLowerCase().includes(q))
      .map((f) => ({ title: f.title, sub: `${f.cat} · ${f.tag}`, link: "#latest" }));
    return [...fromHot, ...fromFeed].slice(0, 6);
  }, [kw]);

  useEffect(() => {
    const close = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setFocus(false);
    };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, []);

  const pick = (link: string) => {
    setFocus(false);
    setKw("");
    document.querySelector(link)?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <header
      className={`fixed inset-x-0 top-0 z-50 transition-all duration-300 ${
        scrolled || menuOpen
          ? "border-b border-background-200/80 bg-background-50/90 backdrop-blur-md"
          : "bg-transparent"
      }`}
    >
      <div className="mx-auto flex h-16 max-w-content items-center gap-4 px-4 md:px-6">
        <a href="#top" className="flex shrink-0 items-center gap-2.5" aria-label="吃瓜图集 首页">
          <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-primary-500 text-background-50">
            <i className="ri-chat-history-line text-xl" />
          </span>
          <span className="flex flex-col leading-none">
            <span className="font-heading text-lg font-bold text-foreground-950">吃瓜图集</span>
            <span className="text-[10px] text-foreground-400">按事件整理截图脉络</span>
          </span>
        </a>

        <nav className="hidden flex-1 items-center justify-center gap-1 md:flex" aria-label="主导航">
          {NAV.map((n) => (
            <a
              key={n.href}
              href={n.href}
              className="whitespace-nowrap rounded-full px-3.5 py-2 text-sm text-foreground-600 transition hover:bg-background-200/70 hover:text-foreground-950"
            >
              {n.label}
            </a>
          ))}
        </nav>

        <div ref={wrapRef} className="relative ml-auto md:ml-0">
          <div className="flex h-10 w-40 items-center gap-2 rounded-full border border-background-300/70 bg-background-100/70 px-3 transition focus-within:border-primary-300 md:w-56">
            <i className="ri-search-line text-sm text-foreground-400" />
            <input
              value={kw}
              onChange={(e) => setKw(e.target.value)}
              onFocus={() => setFocus(true)}
              placeholder="搜索事件 / 截图关键词"
              className="w-full bg-transparent text-sm text-foreground-800 outline-none placeholder:text-foreground-400"
            />
            {kw && (
              <button onClick={() => setKw("")} aria-label="清空搜索" className="text-foreground-400 hover:text-foreground-700">
                <i className="ri-close-circle-fill text-sm" />
              </button>
            )}
          </div>

          {focus && (
            <div className="absolute right-0 top-12 w-72 overflow-hidden rounded-2xl border border-background-200 bg-background-50 p-2 shadow-lg md:w-80">
              {results.length > 0 ? (
                <ul>
                  {results.map((r, i) => (
                    <li key={i}>
                      <button
                        onClick={() => pick(r.link)}
                        className="flex w-full flex-col items-start gap-0.5 rounded-xl px-3 py-2.5 text-left transition hover:bg-background-100"
                      >
                        <span className="line-clamp-1 text-sm font-medium text-foreground-900">{r.title}</span>
                        <span className="text-xs text-foreground-400">{r.sub}</span>
                      </button>
                    </li>
                  ))}
                </ul>
              ) : (
                <div className="px-3 py-6 text-center text-sm text-foreground-400">
                  {kw ? "没有匹配的事件，换个关键词试试" : "输入事件名称或截图关键词，例如：聊天记录、朋友圈、带货"}
                </div>
              )}
            </div>
          )}
        </div>

        <a
          href="#latest"
          className="hidden whitespace-nowrap rounded-full bg-primary-500 px-4 py-2 text-sm font-medium text-background-50 transition hover:bg-primary-600 md:inline-flex"
        >
          去看图集
        </a>

        <button
          className="ml-1 flex h-10 w-10 items-center justify-center rounded-full text-foreground-700 transition hover:bg-background-200/70 md:hidden"
          onClick={() => setMenuOpen((v) => !v)}
          aria-label={menuOpen ? "收起菜单" : "展开菜单"}
          aria-expanded={menuOpen}
        >
          <i className={`text-xl ${menuOpen ? "ri-close-line" : "ri-menu-3-line"}`} />
        </button>
      </div>

      {menuOpen && (
        <nav className="border-t border-background-200/80 bg-background-50 px-4 pb-4 pt-2 md:hidden" aria-label="移动端导航">
          {NAV.map((n) => (
            <a
              key={n.href}
              href={n.href}
              onClick={() => setMenuOpen(false)}
              className="block rounded-xl px-3 py-2.5 text-sm text-foreground-700 transition hover:bg-background-100"
            >
              {n.label}
            </a>
          ))}
        </nav>
      )}
    </header>
  );
}